import { useCallback, useEffect, useState, type ReactNode } from "react"
import { RadioGroup as RadioGroupPrimitive } from "radix-ui"

import { api, type MinhaConta } from "../api"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { ComDica } from "../componentes/ComDica"
import * as Icones from "../componentes/Icones"
import { CarregandoNaSecao, FalhaNaSecao } from "../componentes/Secao"
import { Switch } from "../componentes/Switch"
import { toast } from "../componentes/Toast"
import { cn } from "../lib/cn"
import {
  APARENCIA_TROCADA,
  CORES,
  NOME_DA_COR,
  NOME_DA_FAMILIA,
  NOME_DA_POSICAO,
  NOME_DO_TOM,
  POSICOES,
  TONS,
  aparenciaGuardada,
  mudarAparencia,
  type Aparencia,
  type Contraste,
  type Cor,
  type Familia,
  type Fonte,
  type PosicaoDaBarra,
  type Tom,
} from "../preferencias"
import { PreviaDaFonte, PreviaDaLeitura, PreviaDoTema } from "../componentes/PreviaDoTema"
import { SwitchDeModo } from "../componentes/SwitchDeModo"
import { ToggleGroup, ToggleGroupItem } from "../componentes/ToggleGroup"

type Props = {
  posicao: PosicaoDaBarra
  aoTrocarPosicao: (posicao: PosicaoDaBarra) => void
  email?: string
  telefone?: string | null
  aoIrParaContato?: () => void
}

const TITULO = "m-0 flex items-center gap-1.5 text-sm font-semibold text-(--tinta)"
const ROTULO_DO_GRUPO = "m-0 flex items-center gap-1 text-xs font-medium tracking-wide text-(--tinta-2) uppercase"

export function Preferencias({ posicao, aoTrocarPosicao, email, telefone = null, aoIrParaContato }: Props) {
  return (
    <section data-slot="preferencias" className="flex flex-col divide-y divide-(--borda)">
      {email !== undefined && (
        <div className="flex flex-col gap-3 px-6 py-5">
          <h3 className={TITULO}>
            Avisos
            <ComDica dica="Os avisos aparecem sempre na caixa de avisos do sistema. Aqui você escolhe se também quer recebê-los por e-mail e por WhatsApp." />
          </h3>
          <AvisosDaConta email={email} telefone={telefone} aoIrParaContato={aoIrParaContato} />
        </div>
      )}
      <div className="flex flex-col gap-4 px-6 py-5">
        <h3 className={TITULO}>
          Aparência
          <ComDica dica="Vale para este navegador, e a troca é na hora." />
        </h3>
        <EscolhasDaAparencia />
        <div className="flex flex-col gap-2">
          <p className={ROTULO_DO_GRUPO} id="rotulo-posicao-da-barra">
            Posição da barra de navegação
          </p>
          <CartoesDePosicao posicao={posicao} aoTrocarPosicao={aoTrocarPosicao} />
        </div>
      </div>
    </section>
  )
}

function AvisosDaConta({ email, telefone, aoIrParaContato }: { email: string; telefone: string | null; aoIrParaContato?: () => void }) {
  const [conta, definirConta] = useState<MinhaConta | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [salvando, definirSalvando] = useState(false)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .minhaConta()
      .then(definirConta)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  if (conta === null) return falha !== null ? <FalhaNaSecao mensagem={falha} aoTentar={carregar} /> : <CarregandoNaSecao />

  const salvar = async (mudanca: { avisar_por_email?: boolean; avisar_por_whatsapp?: boolean }, frase: string) => {
    definirSalvando(true)
    try {
      definirConta(await api.mudarMinhaConta(mudanca))
      toast.success(frase)
    } catch (erro) {
      toast.error(mensagemDeRecusa(erro))
    } finally {
      definirSalvando(false)
    }
  }
  const semTelefone = telefone === null || telefone.trim() === ""

  return (
    <div className="flex flex-col divide-y divide-(--borda) rounded-md border border-(--borda)">
      <LinhaDoAviso
        id="aviso-por-email"
        rotulo="Por e-mail"
        descricao={`Vai para ${email}.`}
        ligado={conta.avisar_por_email}
        desabilitado={salvando}
        aoMudar={(ligar) => void salvar({ avisar_por_email: ligar }, ligar ? "Avisos por e-mail ligados" : "Avisos por e-mail desligados")}
      />
      <LinhaDoAviso
        id="aviso-por-whatsapp"
        rotulo="Por WhatsApp"
        descricao={semTelefone ? "Falta o telefone para receber pelo WhatsApp." : `Vai para ${telefone}.`}
        ligado={!semTelefone && conta.avisar_por_whatsapp}
        desabilitado={salvando || semTelefone}
        aoMudar={(ligar) => void salvar({ avisar_por_whatsapp: ligar }, ligar ? "Avisos por WhatsApp ligados" : "Avisos por WhatsApp desligados")}
        acao={
          semTelefone && aoIrParaContato !== undefined ? (
            <Button type="button" variant="link" size="sm" className="h-auto self-start px-0" onClick={aoIrParaContato}>
              Preencher o telefone
            </Button>
          ) : undefined
        }
      />
    </div>
  )
}

function LinhaDoAviso({
  id,
  rotulo,
  descricao,
  ligado,
  desabilitado,
  aoMudar,
  acao,
}: {
  id: string
  rotulo: string
  descricao: string
  ligado: boolean
  desabilitado: boolean
  aoMudar: (ligar: boolean) => void
  acao?: ReactNode
}) {
  return (
    <div className="flex items-start justify-end gap-3 px-4 py-3">
      <div className="flex flex-1 flex-col gap-0.5">
        <label htmlFor={id} className="cursor-pointer text-sm font-medium text-(--tinta)">
          {rotulo}
        </label>
        <p id={`${id}-descricao`} className="m-0 text-sm text-(--tinta-2) wrap-anywhere">
          {descricao}
        </p>
        {acao}
      </div>
      <Switch
        id={id}
        className="mt-0.5"
        aria-label={rotulo}
        aria-describedby={`${id}-descricao`}
        checked={ligado}
        disabled={desabilitado}
        onCheckedChange={aoMudar}
      />
    </div>
  )
}

const CARTAO =
  "relative flex w-[150px] cursor-pointer flex-col gap-2 rounded-(--raio-2) border border-(--borda) bg-(--painel) p-2 text-left hover:border-(--borda-forte) focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none data-[state=checked]:border-(--marca) data-[state=checked]:ring-1 data-[state=checked]:ring-(--marca)"

const MARCA_DO_ESCOLHIDO = "absolute top-1 right-1 flex rounded-full border border-(--marca) bg-(--painel) p-0.5 text-(--marca)"

function CartoesDePosicao({ posicao, aoTrocarPosicao }: { posicao: PosicaoDaBarra; aoTrocarPosicao: (posicao: PosicaoDaBarra) => void }) {
  return (
    <RadioGroupPrimitive.Root
      aria-labelledby="rotulo-posicao-da-barra"
      orientation="horizontal"
      className="flex flex-wrap gap-3"
      value={posicao}
      onValueChange={(valor) => aoTrocarPosicao(valor as PosicaoDaBarra)}
    >
      {POSICOES.map((p) => (
        <RadioGroupPrimitive.Item key={p} value={p} aria-label={NOME_DA_POSICAO[p]} className={CARTAO}>
          <DesenhoDaPosicao posicao={p} />
          <span className="text-(length:--texto-sm) text-(--tinta)">{NOME_DA_POSICAO[p]}</span>
          <RadioGroupPrimitive.Indicator className={MARCA_DO_ESCOLHIDO}>
            <Icones.Validada tamanho={12} />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  )
}

const NOME_DA_FONTE: Record<Fonte, string> = { normal: "Normal", grande: "Grande" }
const NOME_DO_CONTRASTE: Record<Contraste, string> = { normal: "Normal", alto: "Alto contraste" }

function useAparencia() {
  const [aparencia, definirAparencia] = useState<Aparencia>(() => aparenciaGuardada())
  useEffect(() => {
    const aoTrocar = (evento: Event) => definirAparencia((evento as CustomEvent<Aparencia>).detail)
    window.addEventListener(APARENCIA_TROCADA, aoTrocar)
    return () => window.removeEventListener(APARENCIA_TROCADA, aoTrocar)
  }, [])
  const mudar = useCallback((parcial: Partial<Aparencia>) => {
    if (mudarAparencia(parcial)) toast.success("Preferência guardada")
    else toast.warning("Este navegador não deixou guardar a preferência: ela vale até fechar a página.")
  }, [])
  return { aparencia, mudar }
}

const ROTULO_DO_LADO = "text-xs font-medium text-(--tinta-2)"

function GrupoComLado({ id, rotulo, lado, children }: { id: string; rotulo: string; lado?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <p className={ROTULO_DO_GRUPO} id={id}>
          {rotulo}
        </p>
        {lado}
      </div>
      {children}
    </div>
  )
}

function LadoSegmentado<T extends string>({
  rotulo,
  valor,
  opcoes,
  aoEscolher,
}: {
  rotulo: string
  valor: T
  opcoes: { valor: T; rotulo: string }[]
  aoEscolher: (valor: T) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={ROTULO_DO_LADO}>{rotulo}</span>
      <ToggleGroup type="single" aria-label={rotulo} value={valor} onValueChange={(novo) => novo !== "" && aoEscolher(novo as T)}>
        {opcoes.map((opcao) => (
          <ToggleGroupItem key={opcao.valor} value={opcao.valor}>
            {opcao.rotulo}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}

function EscolhasDaAparencia() {
  const { aparencia: a, mudar } = useAparencia()
  return (
    <>
      <GrupoComLado
        id="rotulo-cor"
        rotulo="Cor"
        lado={
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div data-slot="lado-do-modo" className="flex items-center gap-2">
              <span className={ROTULO_DO_LADO}>Modo</span>
              <SwitchDeModo escuro={a.modo === "escuro"} aoMudar={(escuro) => mudar({ modo: escuro ? "escuro" : "claro" })} />
            </div>
            <LadoSegmentado<Tom>
              rotulo="Tonalidade"
              valor={a.tom}
              opcoes={TONS.map((t) => ({ valor: t, rotulo: NOME_DO_TOM[t] }))}
              aoEscolher={(tom) => mudar({ tom })}
            />
          </div>
        }
      >
        <RadioGroupPrimitive.Root
          aria-labelledby="rotulo-cor"
          orientation="horizontal"
          className="flex flex-wrap gap-3"
          value={a.cor}
          onValueChange={(valor) => mudar({ cor: valor as Cor })}
        >
          {CORES.map((cor) => (
            <RadioGroupPrimitive.Item key={cor} value={cor} aria-label={NOME_DA_COR[cor]} className={CARTAO}>
              <PreviaDoTema modo={a.modo} cor={cor} tom={a.tom} />
              <span className="text-(length:--texto-sm) text-(--tinta)">{NOME_DA_COR[cor]}</span>
              <RadioGroupPrimitive.Indicator className={MARCA_DO_ESCOLHIDO}>
                <Icones.Validada tamanho={12} />
              </RadioGroupPrimitive.Indicator>
            </RadioGroupPrimitive.Item>
          ))}
        </RadioGroupPrimitive.Root>
      </GrupoComLado>

      <GrupoComLado id="rotulo-familia" rotulo="Fonte">
        <RadioGroupPrimitive.Root
          aria-labelledby="rotulo-familia"
          orientation="horizontal"
          className="flex flex-wrap gap-3"
          value={a.familia}
          onValueChange={(valor) => mudar({ familia: valor as Familia })}
        >
          {(["inter", "plex"] as const).map((familia) => (
            <RadioGroupPrimitive.Item key={familia} value={familia} aria-label={NOME_DA_FAMILIA[familia]} className={cn(CARTAO, "w-[260px] max-w-full")}>
              <PreviaDaFonte modo={a.modo} cor={a.cor} tom={a.tom} familia={familia} />
              <span className="text-(length:--texto-sm) text-(--tinta)">{NOME_DA_FAMILIA[familia]}</span>
              <RadioGroupPrimitive.Indicator className={MARCA_DO_ESCOLHIDO}>
                <Icones.Validada tamanho={12} />
              </RadioGroupPrimitive.Indicator>
            </RadioGroupPrimitive.Item>
          ))}
        </RadioGroupPrimitive.Root>
      </GrupoComLado>

      <GrupoComLado
        id="rotulo-fonte"
        rotulo="Tamanho da letra"
        lado={
          <LadoSegmentado<Contraste>
            rotulo="Contraste"
            valor={a.contraste}
            opcoes={(["normal", "alto"] as const).map((c) => ({ valor: c, rotulo: NOME_DO_CONTRASTE[c] }))}
            aoEscolher={(contraste) => mudar({ contraste })}
          />
        }
      >
        <RadioGroupPrimitive.Root
          aria-labelledby="rotulo-fonte"
          orientation="horizontal"
          className="flex flex-wrap gap-3"
          value={a.fonte}
          onValueChange={(valor) => mudar({ fonte: valor as Fonte })}
        >
          {(["normal", "grande"] as const).map((fonte) => (
            <RadioGroupPrimitive.Item key={fonte} value={fonte} aria-label={NOME_DA_FONTE[fonte]} className={cn(CARTAO, "w-[260px] max-w-full")}>
              <PreviaDaLeitura modo={a.modo} cor={a.cor} tom={a.tom} grande={fonte === "grande"} />
              <span className="text-(length:--texto-sm) text-(--tinta)">{NOME_DA_FONTE[fonte]}</span>
              <RadioGroupPrimitive.Indicator className={MARCA_DO_ESCOLHIDO}>
                <Icones.Validada tamanho={12} />
              </RadioGroupPrimitive.Indicator>
            </RadioGroupPrimitive.Item>
          ))}
        </RadioGroupPrimitive.Root>
      </GrupoComLado>
    </>
  )
}

const LINHA_DO_DESENHO = "h-1 rounded-(--raio-pequeno) bg-(--borda-forte)"

function DesenhoDaPosicao({ posicao }: { posicao: PosicaoDaBarra }) {
  const temTrilho = posicao !== "topo"
  const trilhoADireita = posicao === "direita" || posicao === "topo-direita"
  const trilho = (
    <div
      data-peca={trilhoADireita ? "trilho-direita" : "trilho-esquerda"}
      className={cn("flex w-[22%] flex-col gap-1 border-(--borda) bg-(--nav-fundo) p-1", trilhoADireita ? "border-l" : "border-r")}
    >
      <div className={LINHA_DO_DESENHO} />
      <div className={LINHA_DO_DESENHO} />
      <div className={LINHA_DO_DESENHO} />
    </div>
  )
  return (
    <div aria-hidden="true" className="flex aspect-[1.6] w-full flex-col overflow-hidden rounded-(--raio) border border-(--borda) bg-(--fundo)">
      <div data-peca="faixa" className="h-[18%] bg-(--marca)" />
      <div className="flex min-h-0 flex-1">
        {temTrilho && !trilhoADireita && trilho}
        <div data-peca="corpo" className="flex flex-1 flex-col gap-1.5 p-2">
          <div className={cn(LINHA_DO_DESENHO, "w-3/4")} />
          <div className={cn(LINHA_DO_DESENHO, "w-1/2")} />
          <div className={cn(LINHA_DO_DESENHO, "w-2/3")} />
        </div>
        {temTrilho && trilhoADireita && trilho}
      </div>
    </div>
  )
}
